import { ActivatedRoute, Router } from '@angular/router';
import { Component, HostListener, OnInit } from '@angular/core';
import { CommonModule, TitleCasePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';

import { LawyerHistoryService } from '../service/lawyer-history.service';
import { LawyersService } from '../service/lawyers-service';
import { ToastrService } from 'ngx-toastr';
import { firstValueFrom } from 'rxjs';

@Component({
  selector: 'app-lawyer-history',
  templateUrl: './lawyer-history.component.html',
   standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TitleCasePipe
  ],
  styleUrls: [
    '../client-history/client-history.component.css',
    './lawyer-history.component.css'
  ]
})
export class LawyerHistoryComponent implements OnInit {

  // ─── Identity ─────────────────────────────────────────────────────────────
  lawyerId: string = '';

  // The professional's real `uid` — distinct from `lawyerId` (which is the
  // `user_id` the history APIs use). The lawyer-history summary API only
  // ever returns user_id-keyed data, so this can't be derived from it; it
  // has to arrive from wherever the person navigated here from (the lawyers
  // list already has `professional.uid` before it routes to this screen).
  lawyerUid: string = '';

  // ─── Summary ──────────────────────────────────────────────────────────────
  lawyer: any = null;
  summary: any = null;
  summaryLoading: boolean = false;

  // ─── Tabs ─────────────────────────────────────────────────────────────────
  //
  // Only one tab is on screen at a time, so the toolbar, pagination and menu
  // handlers all work against `state` - the active tab's slice.
  activeTab: string = 'profile';

  appt: any = {
    rows: [],
    loading: false,
    loaded: false,
    pagination: { page: 1, limit: 10, total: 0, total_pages: 0 },
    startDate: '',
    endDate: '',
    draftStart: '',
    draftEnd: '',
    status: '',
    mode: '',
    search: '',
    draftMode: '',
    draftSearch: ''
  };

  settle: any = {
    rows: [],
    loading: false,
    loaded: false,
    pagination: { page: 1, limit: 10, total: 0, total_pages: 0 },
    startDate: '',
    endDate: '',
    draftStart: '',
    draftEnd: '',
    status: '',
    search: '',
    draftSearch: ''
  };

  act: any = {
    rows: [],
    loading: false,
    loaded: false,
    pagination: { page: 1, limit: 10, total: 0, total_pages: 0 },
    // Load More appends pages, so the range label counts from the first page
    // still on screen rather than from the current one.
    firstPage: 1,
    startDate: '',
    endDate: '',
    draftStart: '',
    draftEnd: '',
    status: ''
  };

  // Earnings is a single figure set, not a table, so it carries only its own
  // date range.
  earn: any = {
    rows: [],
    loading: false,
    loaded: false,
    pagination: { page: 1, limit: 10, total: 0, total_pages: 0 },
    startDate: '',
    endDate: '',
    draftStart: '',
    draftEnd: '',
    status: '',
    data: null
  };

  private readonly slices: any = {
    appointments: 'appt',
    settlements: 'settle',
    earnings: 'earn',
    activity: 'act'
  };

  get state(): any {
    return (this as any)[this.slices[this.activeTab] || 'appt'];
  }

  // ─── Profile Details tab ────────────────────────────────────────────────
  //
  // Ported straight from the lawyer list's "View Details" action panel:
  // same API call (LawyersService.getProfessionalDetail), same response
  // shape and the same loading/error handling. It is not part of `slices`
  // above since it is a single record, not a paginated table.
  profileData: any = null;
  profileLoading: boolean = false;
  profileLoaded: boolean = false;

  // ─── Recent settlements strip on the appointments tab ─────────────────────
  recentSettlements: any[] = [];
  recentTotal: number = 0;

  // ─── Filter options ───────────────────────────────────────────────────────

  // A confirmed appointment is never shown as "confirmed" - the clock decides
  // between upcoming, ongoing and completed. Unpaid and abandoned bookings are
  // excluded by the API, so they are not offered as filters.
  statusOptions = [
    { value: '', label: 'All Statuses' },
    { value: 'completed', label: 'Completed' },
    { value: 'ongoing', label: 'Ongoing' },
    { value: 'upcoming', label: 'Upcoming' },
    { value: 'cancelled', label: 'Cancelled' },
    // { value: 'cancellation_pending', label: 'Cancellation Pending' },
    // { value: 'no_show', label: 'No Show' }
  ];

  consultationModeOptions = [
    { value: '', label: 'All Modes' },
    { value: 'video_call', label: 'Video Call' },
    { value: 'phone_call', label: 'Phone Call' },
    { value: 'online', label: 'Online' },
    { value: 'in_person', label: 'In Person' }
  ];

  payoutStatusOptions = [
    { value: '', label: 'All Status' },
    { value: 'paid', label: 'Paid' },
    { value: 'processing', label: 'Processing' },
    { value: 'pending', label: 'Pending' },
    { value: 'failed', label: 'Failed' },
    { value: 'reversed', label: 'Reversed' }
  ];

  eventTypeOptions = [
    { value: '', label: 'All Events' },
    { value: 'appointment_booked', label: 'Appointment Booked' },
    { value: 'appointment_confirmed', label: 'Appointment Confirmed' },
    { value: 'appointment_completed', label: 'Appointment Completed' },
    { value: 'appointment_cancelled', label: 'Appointment Cancelled' },
    { value: 'payment_successful', label: 'Payment Successful' },
    { value: 'refund_processed', label: 'Refund Processed' },
    { value: 'refund_failed', label: 'Refund Failed' },
    { value: 'payout_created', label: 'Payout Created' },
    { value: 'payout_completed', label: 'Payout Completed' },
    { value: 'payout_failed', label: 'Payout Failed' },
    { value: 'payout_reversed', label: 'Payout Reversed' },
    {
      value: 'settlement_invoices_generated',
      label: 'Settlement Invoices Generated'
    }
  ];

  pageSizeOptions = [10, 25, 50, 100];

  // ─── Toolbar menus ────────────────────────────────────────────────────────
  showDateMenu: boolean = false;
  showFilterMenu: boolean = false;

  // Active Devices popover on the Profile Details tab's summary strip.
  showDevicesMenu: boolean = false;

  // ─── Detail panel ─────────────────────────────────────────────────────────
  detailKind: string | null = null;
  detailId: string | null = null;
  detail: any = null;
  detailLoading: boolean = false;

  exporting: boolean = false;

  private readonly statusLabels: any = {
    pending_payment: 'Payment Pending',
    cancellation_pending: 'Cancellation Pending',
    no_show: 'No Show',
    resheduled: 'Rescheduled'
  };

  private readonly modeLabels: any = {
    video_call: 'Video Call',
    phone_call: 'Phone Call',
    in_person: 'In Person',
    online: 'Online',
    chat: 'Chat',
    upi: 'UPI',
    emi: 'EMI',
    netbanking: 'Netbanking'
  };

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private lawyerHistoryService: LawyerHistoryService,
    private lawyersService: LawyersService,
    private toastr: ToastrService
  ) {}

  ngOnInit(): void {
    this.lawyerId = this.route.snapshot.paramMap.get('lawyerId') || '';
    this.lawyerUid = this.route.snapshot.queryParamMap.get('uid') || '';

    if (!this.lawyerId) {
      this.router.navigate(['/admin/lexiz-lawyers']);
      return;
    }

    this.loadSummary();
    this.loadProfileDetail();
    this.loadRecentSettlements();
  }

  @HostListener('document:click')
  closeMenus(): void {
    this.showDateMenu = false;
    this.showFilterMenu = false;
    this.showDevicesMenu = false;
  }

  // ═══════════════════════════════════════════════════════════
  //  API
  // ═══════════════════════════════════════════════════════════

  loadSummary(): void {
    this.summaryLoading = true;

    this.lawyerHistoryService.getSummary(this.lawyerId).subscribe({
      next: (resp: any) => {
        this.summary = resp?.['data'] || null;
        this.lawyer = this.summary?.['lawyer'] || null;
        this.summaryLoading = false;
      },
      error: (err: any) => {
        this.summaryLoading = false;
        this.toastr.error(err?.error?.msg || 'Unable to load lawyer summary');
      }
    });
  }

  // Same call, same response handling as the lawyer list's "View Details"
  // action (LawyersService.getProfessionalDetail) — only moved here.
  //
  // NOTE: that endpoint is keyed by the professional's `uid`, which the
  // lawyer-history summary API does not return (it only carries user_id
  // data). `lawyerUid` has to come in via the `uid` query param on this
  // route — see the note on `lawyerUid` above.
  loadProfileDetail(): void {
    if (!this.lawyerUid) {
      this.profileData = null;
      this.profileLoading = false;
      this.profileLoaded = true;
      this.toastr.error('Missing lawyer uid — unable to load profile details', 'Error');
      return;
    }

    this.profileLoading = true;

    this.lawyersService
      .getProfessionalDetail(this.lawyerUid)
      .subscribe({
        next: (response: any) => {
          if (response && !response.error && response.details) {
            this.profileData = response.details;
          } else {
            this.profileData = null;
            this.toastr.error('Failed to load professional details', 'Error');
          }
          this.profileLoading = false;
          this.profileLoaded = true;
        },
        error: (_error: any) => {
          this.profileData = null;
          this.profileLoading = false;
          this.profileLoaded = true;
          this.toastr.error('Failed to load professional details', 'Error');
        }
      });
  }

  private appointmentParams(): any {
    return {
      page: this.appt.pagination.page,
      limit: this.appt.pagination.limit,
      status: this.appt.status,
      consultation_mode: this.appt.mode,
      search: this.appt.search,
      start_date: this.appt.startDate,
      end_date: this.appt.endDate
    };
  }

  private settlementParams(): any {
    return {
      page: this.settle.pagination.page,
      limit: this.settle.pagination.limit,
      status: this.settle.status,
      search: this.settle.search,
      start_date: this.settle.startDate,
      end_date: this.settle.endDate
    };
  }

  private activityParams(): any {
    return {
      page: this.act.pagination.page,
      limit: this.act.pagination.limit,
      type: this.act.status,
      start_date: this.act.startDate,
      end_date: this.act.endDate
    };
  }

  loadAppointments(): void {
    this.appt.loading = true;

    this.lawyerHistoryService
      .getAppointments(this.lawyerId, this.appointmentParams())
      .subscribe({
        next: (resp: any) => {
          this.appt.rows = resp?.['data'] || [];
          this.appt.pagination = resp?.['pagination'] || this.appt.pagination;
          this.appt.loading = false;
          this.appt.loaded = true;
        },
        error: (err: any) => {
          this.appt.rows = [];
          this.appt.loading = false;
          this.appt.loaded = true;
          this.toastr.error(
            err?.error?.msg || 'Unable to load appointment history'
          );
        }
      });
  }

  loadSettlements(): void {
    this.settle.loading = true;

    this.lawyerHistoryService
      .getSettlements(this.lawyerId, this.settlementParams())
      .subscribe({
        next: (resp: any) => {
          this.settle.rows = resp?.['data'] || [];
          this.settle.pagination =
            resp?.['pagination'] || this.settle.pagination;
          this.settle.loading = false;
          this.settle.loaded = true;
        },
        error: (err: any) => {
          this.settle.rows = [];
          this.settle.loading = false;
          this.settle.loaded = true;
          this.toastr.error(err?.error?.msg || 'Unable to load settlements');
        }
      });
  }

  // The strip under the appointments table: the newest payouts only.
  loadRecentSettlements(): void {
    this.lawyerHistoryService
      .getSettlements(this.lawyerId, { page: 1, limit: 5 })
      .subscribe({
        next: (resp: any) => {
          this.recentSettlements = resp?.['data'] || [];
          this.recentTotal = resp?.['pagination']?.['total'] || 0;
        },
        error: () => {
          this.recentSettlements = [];
          this.recentTotal = 0;
        }
      });
  }

  loadEarnings(): void {
    this.earn.loading = true;

    this.lawyerHistoryService
      .getEarnings(this.lawyerId, {
        start_date: this.earn.startDate,
        end_date: this.earn.endDate
      })
      .subscribe({
        next: (resp: any) => {
          this.earn.data = resp?.['data'] || null;
          // The shared empty/skeleton states key off `rows`.
          this.earn.rows = this.earn.data ? [this.earn.data] : [];
          this.earn.loading = false;
          this.earn.loaded = true;
        },
        error: (err: any) => {
          this.earn.data = null;
          this.earn.rows = [];
          this.earn.loading = false;
          this.earn.loaded = true;
          this.toastr.error(
            err?.error?.msg || 'Unable to load the earnings summary'
          );
        }
      });
  }

  // `append` is Load More: the next page is added to what is on screen.
  loadActivity(append: boolean = false): void {
    this.act.loading = !append;
    this.act.loadingMore = append;

    if (append) {
      this.act.pagination.page = this.act.pagination.page + 1;
    } else {
      this.act.firstPage = this.act.pagination.page;
    }

    this.lawyerHistoryService
      .getActivity(this.lawyerId, this.activityParams())
      .subscribe({
        next: (resp: any) => {
          const rows = resp?.['data'] || [];

          this.act.rows = append ? [...this.act.rows, ...rows] : rows;
          this.act.pagination = resp?.['pagination'] || this.act.pagination;
          this.act.loading = false;
          this.act.loadingMore = false;
          this.act.loaded = true;
        },
        error: (err: any) => {
          if (!append) {
            this.act.rows = [];
          }

          this.act.loading = false;
          this.act.loadingMore = false;
          this.act.loaded = true;
          this.toastr.error(
            err?.error?.msg || 'Unable to load the activity timeline'
          );
        }
      });
  }

  get canLoadMore(): boolean {
    return (
      this.activeTab === 'activity' &&
      !this.act.loading &&
      this.act.rows.length > 0 &&
      this.act.pagination.page < (this.act.pagination.total_pages || 0)
    );
  }

  reload(): void {
    switch (this.activeTab) {
      case 'settlements':
        this.loadSettlements();
        break;
      case 'earnings':
        this.loadEarnings();
        break;
      case 'activity':
        this.loadActivity();
        break;
      default:
        this.loadAppointments();
    }
  }

  // ═══════════════════════════════════════════════════════════
  //  Detail panel
  // ═══════════════════════════════════════════════════════════

  openAppointment(row: any): void {
    this.openDetail(
      'appointment',
      row.appointment_id,
      this.lawyerHistoryService.getAppointmentDetail(
        this.lawyerId,
        row.appointment_id
      ),
      'Unable to load appointment details'
    );
  }

  openPayout(row: any): void {
    this.openDetail(
      'payout',
      row.payout_id,
      this.lawyerHistoryService.getSettlementDetail(
        this.lawyerId,
        row.payout_id
      ),
      'Unable to load payout details'
    );
  }

  // Activity has no per-event endpoint: every field the panel shows is
  // already on the row, so the panel reads it straight off.
  openActivity(row: any): void {
    this.detailKind = 'activity';
    this.detailId = row.event_id;
    this.detail = row;
    this.detailLoading = false;
  }

  private openDetail(
    kind: string,
    id: string,
    request: any,
    errorMessage: string
  ): void {
    this.detailKind = kind;
    this.detailId = id;
    this.detail = null;
    this.detailLoading = true;

    request.subscribe({
      next: (resp: any) => {
        this.detail = resp?.['data'] || null;
        this.detailLoading = false;
      },
      error: (err: any) => {
        this.detailLoading = false;
        this.toastr.error(err?.error?.msg || errorMessage);
      }
    });
  }

  closeDetail(): void {
    this.detailKind = null;
    this.detailId = null;
    this.detail = null;
  }

  isSelected(kind: string, id: string): boolean {
    return this.detailKind === kind && this.detailId === id;
  }

  // From an appointment or an activity event to the payout that settled it.
  viewRelatedPayout(payoutId?: string): void {
    const id = payoutId || this.detail?.payout_id;

    if (!id) {
      return;
    }

    this.activeTab = 'settlements';

    if (!this.settle.loaded) {
      this.loadSettlements();
    }

    this.openPayout({ payout_id: id });
  }

  viewRelatedAppointment(appointmentId?: string): void {
    const id = appointmentId || this.detail?.appointment_id;

    if (!id) {
      return;
    }

    this.activeTab = 'appointments';

    if (!this.appt.loaded) {
      this.loadAppointments();
    }

    this.openAppointment({ appointment_id: id });
  }

  // ═══════════════════════════════════════════════════════════
  //  Tabs
  // ═══════════════════════════════════════════════════════════

  selectTab(tab: string): void {
    if (this.activeTab === tab) {
      return;
    }

    this.activeTab = tab;
    this.showDateMenu = false;
    this.showFilterMenu = false;
    this.closeDetail();

    if (tab === 'profile') {
      if (!this.profileLoaded) {
        this.loadProfileDetail();
      }
      return;
    }

    if (!this.state.loaded) {
      this.reload();
    }
  }

  backToLawyers(): void {
    this.router.navigate(['/admin/lexiz-lawyers']);
  }

  // ═══════════════════════════════════════════════════════════
  //  Filters
  // ═══════════════════════════════════════════════════════════

  toggleDateMenu(event: Event): void {
    event.stopPropagation();

    this.showFilterMenu = false;
    this.showDateMenu = !this.showDateMenu;

    this.state.draftStart = this.state.startDate;
    this.state.draftEnd = this.state.endDate;
  }

  applyDateRange(): void {
    const state = this.state;

    if (state.draftStart && state.draftEnd && state.draftStart > state.draftEnd) {
      this.toastr.warning('The start date must come before the end date');
      return;
    }

    state.startDate = state.draftStart;
    state.endDate = state.draftEnd;
    this.showDateMenu = false;

    this.resetToFirstPage();
  }

  clearDateRange(): void {
    const state = this.state;

    state.startDate = '';
    state.endDate = '';
    state.draftStart = '';
    state.draftEnd = '';
    this.showDateMenu = false;

    this.resetToFirstPage();
  }

  onFilterChange(): void {
    this.resetToFirstPage();
  }

  // Active Devices popover on the Profile Details tab.
  toggleDevicesMenu(event: Event): void {
    event.stopPropagation();

    this.showDateMenu = false;
    this.showFilterMenu = false;
    this.showDevicesMenu = !this.showDevicesMenu;
  }

  toggleFilterMenu(event: Event): void {
    event.stopPropagation();

    this.showDateMenu = false;
    this.showFilterMenu = !this.showFilterMenu;

    if (this.activeTab === 'settlements') {
      this.settle.draftSearch = this.settle.search;
    } else {
      this.appt.draftMode = this.appt.mode;
      this.appt.draftSearch = this.appt.search;
    }
  }

  applyFilters(): void {
    if (this.activeTab === 'settlements') {
      this.settle.search = this.settle.draftSearch;
    } else {
      this.appt.mode = this.appt.draftMode;
      this.appt.search = this.appt.draftSearch;
    }

    this.showFilterMenu = false;
    this.resetToFirstPage();
  }

  resetFilters(): void {
    if (this.activeTab === 'settlements') {
      this.settle.search = '';
      this.settle.draftSearch = '';
    } else {
      this.appt.mode = '';
      this.appt.search = '';
      this.appt.draftMode = '';
      this.appt.draftSearch = '';
    }

    this.showFilterMenu = false;
    this.resetToFirstPage();
  }

  get activeFilterCount(): number {
    if (this.activeTab === 'settlements') {
      return this.settle.search ? 1 : 0;
    }

    return (this.appt.mode ? 1 : 0) + (this.appt.search ? 1 : 0);
  }

  // Earnings has no rows to filter and activity filters through its event
  // dropdown, so neither carries a filter popover.
  get hasFilterMenu(): boolean {
    return this.activeTab === 'appointments' || this.activeTab === 'settlements';
  }

  get hasStatusFilter(): boolean {
    return this.activeTab !== 'earnings';
  }

  get statusOptionsForTab(): any[] {
    switch (this.activeTab) {
      case 'settlements':
        return this.payoutStatusOptions;
      case 'activity':
        return this.eventTypeOptions;
      default:
        return this.statusOptions;
    }
  }

  get statusPlaceholder(): string {
    switch (this.activeTab) {
      case 'settlements':
        return 'All Status';
      case 'activity':
        return 'All Events';
      default:
        return 'Status';
    }
  }

  get tabTitle(): string {
    switch (this.activeTab) {
      case 'profile':
        return 'Profile Details';
      case 'settlements':
        return 'Settlements';
      case 'earnings':
        return 'Earnings Summary';
      case 'activity':
        return 'Activity Timeline';
      default:
        return 'Appointment History';
    }
  }

  get rowNoun(): string {
    switch (this.activeTab) {
      case 'settlements':
        return 'payouts';
      case 'activity':
        return 'activities';
      default:
        return 'appointments';
    }
  }

  get dateRangeLabel(): string {
    const state = this.state;

    if (!state.startDate && !state.endDate) {
      return 'All dates';
    }

    const from = state.startDate ? this.formatDate(state.startDate) : 'Any';
    const to = state.endDate ? this.formatDate(state.endDate) : 'Any';

    return `${from} - ${to}`;
  }

  private resetToFirstPage(): void {
    this.state.pagination.page = 1;
    this.closeDetail();
    this.reload();
  }

  // ═══════════════════════════════════════════════════════════
  //  Export
  // ═══════════════════════════════════════════════════════════

  get canExport(): boolean {
    return this.activeTab === 'settlements';
  }

  // Exports every payout the current filters match, not just the page on
  // screen, so the file is walked page by page at the API's maximum size.
  async exportRows(): Promise<void> {
    if (this.exporting || !this.canExport) {
      return;
    }

    this.exporting = true;

    try {
      const rows: any[] = [];
      const limit = 100;

      let page = 1;
      let total = 0;

      // Stop on the first empty page as well as on the count, so a page that
      // comes back short can never spin this loop forever.
      while (true) {
        const params = { ...this.settlementParams(), page, limit };

        const resp: any = await firstValueFrom(
          this.lawyerHistoryService.getSettlements(this.lawyerId, params)
        );

        const batch: any[] = resp?.['data'] || [];

        rows.push(...batch);
        total = resp?.['pagination']?.['total'] || 0;

        if (!batch.length || rows.length >= total) {
          break;
        }

        page++;
      }

      if (!rows.length) {
        this.toastr.info('There is nothing to export');
        return;
      }

      this.downloadCsv(rows);
    } catch (err: any) {
      this.toastr.error(err?.error?.msg || 'Unable to export the settlements');
    } finally {
      this.exporting = false;
    }
  }

  private downloadCsv(rows: any[]): void {
    const headers = [
      'Payout Date',
      'Payout ID',
      'Appointments',
      'Gross Amount',
      'Lawyer Payable (Net)',
      'Currency',
      'UTR / Reference',
      'Status'
    ];

    const lines = [headers.map(this.csvCell).join(',')];

    rows.forEach(row => {
      lines.push([
        this.formatDateTime(row.payout_date),
        row.razorpay_payout_id || row.payout_id,
        row.appointments,
        row.gross_amount,
        row.net_payable,
        row.currency,
        row.utr,
        this.statusLabel(row.status)
      ].map(this.csvCell).join(','));
    });

    const blob = new Blob(['﻿' + lines.join('\r\n')], {
      type: 'text/csv;charset=utf-8;'
    });

    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    const stamp = new Date().toISOString().slice(0, 10);

    link.href = url;
    link.download =
      `settlements-${this.lawyer?.id || this.lawyerId}-${stamp}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  private csvCell(value: any): string {
    if (value === null || value === undefined) {
      return '""';
    }

    return `"${String(value).replace(/"/g, '""')}"`;
  }

  copy(value: string | null, label: string = 'Value'): void {
    if (!value) {
      return;
    }

    navigator.clipboard?.writeText(value).then(
      () => this.toastr.success(`${label} copied`),
      () => this.toastr.error(`Unable to copy the ${label.toLowerCase()}`)
    );
  }

  // ═══════════════════════════════════════════════════════════
  //  Pagination
  // ═══════════════════════════════════════════════════════════

  getTotalPages(): number {
    return this.state.pagination?.total_pages || 0;
  }

  getPageNumbers(): number[] {
    const total = this.getTotalPages();
    const current = this.state.pagination.page;

    if (total <= 1) {
      return total === 1 ? [1] : [];
    }

    // A five-page window that stays inside the available range.
    let start = Math.max(1, current - 2);
    const end = Math.min(total, start + 4);

    start = Math.max(1, end - 4);

    const pages: number[] = [];

    for (let page = start; page <= end; page++) {
      pages.push(page);
    }

    return pages;
  }

  goToPage(page: number): void {
    if (
      page < 1 ||
      page > this.getTotalPages() ||
      page === this.state.pagination.page
    ) {
      return;
    }

    this.state.pagination.page = page;
    this.closeDetail();
    this.reload();
  }

  goToPrevPage(): void {
    this.goToPage(this.state.pagination.page - 1);
  }

  goToNextPage(): void {
    this.goToPage(this.state.pagination.page + 1);
  }

  changePageSize(limit: any): void {
    this.state.pagination.limit = Number(limit);
    this.state.pagination.page = 1;
    this.closeDetail();
    this.reload();
  }

  getPaginationRange(): string {
    const state = this.state;
    const noun = this.rowNoun;
    const total = state.pagination?.total || 0;

    if (!total) {
      return `No ${noun}`;
    }

    // Activity's Load More appends pages, so count from the first page still
    // on screen rather than from the current one.
    const from = state.firstPage || state.pagination.page;
    const first = (from - 1) * state.pagination.limit + 1;
    const last = Math.min(first + state.rows.length - 1, total);

    return `Showing ${first} to ${last} of ${total} ${noun}`;
  }

  // ═══════════════════════════════════════════════════════════
  //  Display helpers
  // ═══════════════════════════════════════════════════════════

  initials(name: string | null): string {
    if (!name) {
      return '-';
    }

    const parts = name.trim().split(/\s+/);
    const first = parts[0]?.charAt(0) || '';
    const last = parts.length > 1 ? parts[parts.length - 1].charAt(0) : '';

    return (first + last).toUpperCase();
  }

  formatDate(value: string | null): string {
    if (!value) {
      return '-';
    }

    const date = new Date(value);

    if (isNaN(date.getTime())) {
      return '-';
    }

    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  }

  formatTime(value: string | null): string {
    if (!value) {
      return '';
    }

    const date = new Date(value);

    if (isNaN(date.getTime())) {
      return '';
    }

    return date.toLocaleTimeString('en-IN', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  }

  formatDateTime(value: string | null): string {
    if (!value) {
      return '-';
    }

    const date = this.formatDate(value);
    const time = this.formatTime(value);

    return time ? `${date} ${time}` : date;
  }

  // Urgency tier for a "days left" badge — '' (healthy/green), 'mid' (amber)
  // or 'low' (red) — so the color actually conveys how soon the plan ends.
  remainingDaysTier(days: number | null | undefined): string {
    if (days === null || days === undefined || isNaN(days)) {
      return '';
    }

    if (days <= 7) {
      return 'low';
    }

    if (days <= 15) {
      return 'mid';
    }

    return '';
  }

  remainingDays(value: string | null): number | null {
    if (!value) {
      return null;
    }

    const end = new Date(value);

    if (isNaN(end.getTime())) {
      return null;
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    end.setHours(0, 0, 0, 0);

    return Math.ceil((end.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
  }

  money(amount: any, currency: string = 'INR'): string {
    if (amount === null || amount === undefined || amount === '') {
      return '-';
    }

    const value = Number(amount);

    if (isNaN(value)) {
      return '-';
    }

    const symbol = currency === 'INR' ? '₹' : `${currency} `;

    return symbol + value.toLocaleString('en-IN', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    });
  }

  // "Platform Fee (10%)" — the rate is not stored, so it is derived from the
  // amounts themselves and only shown when both sides are known.
  percentOf(part: any, whole: any): string {
    const partValue = Number(part);
    const wholeValue = Number(whole);

    if (!wholeValue || isNaN(partValue) || isNaN(wholeValue)) {
      return '';
    }

    const percent = (partValue / wholeValue) * 100;

    return ` (${Math.round(percent * 10) / 10}%)`;
  }

  label(value: string | null, map: any): string {
    if (!value) {
      return '-';
    }

    if (map[value]) {
      return map[value];
    }

    return value
      .split('_')
      .map(part => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ');
  }

  statusLabel(status: string | null): string {
    return this.label(status, this.statusLabels);
  }

  modeLabel(mode: string | null): string {
    return this.label(mode, this.modeLabels);
  }

  statusClass(status: string | null): string {
    switch ((status || '').toLowerCase()) {
      case 'completed':
        return 'status-completed';
      case 'ongoing':
        return 'status-ongoing';
      case 'upcoming':
      case 'confirmed':
        return 'status-confirmed';
      case 'cancelled':
        return 'status-cancelled';
      case 'pending_payment':
      case 'cancellation_pending':
        return 'status-pending';
      case 'abandoned':
      case 'no_show':
        return 'status-abandoned';
      default:
        return 'status-default';
    }
  }

  paymentStatusClass(status: string | null): string {
    switch ((status || '').toLowerCase()) {
      case 'paid':
      case 'captured':
      case 'processed':
      case 'settled':
      case 'invoiced':
        return 'status-completed';
      case 'pending':
      case 'created':
      case 'initiated':
      case 'processing':
      case 'queued':
        return 'status-pending';
      case 'failed':
      case 'reversed':
        return 'status-cancelled';
      default:
        return 'status-default';
    }
  }

  modeIcon(mode: string | null): string {
    switch ((mode || '').toLowerCase()) {
      case 'video_call':
      case 'online':
        return 'bi-camera-video';
      case 'phone_call':
        return 'bi-telephone';
      case 'in_person':
        return 'bi-person-workspace';
      default:
        return 'bi-chat-dots';
    }
  }

  // The console has no short appointment or payout code, so a row shows the
  // tail of the real id; the full value stays in the cell's tooltip.
  shortId(id: string | null): string {
    if (!id) {
      return '-';
    }

    return id.length > 8 ? `…${id.slice(-8)}` : id;
  }

  timelineIcon(type: string): string {
    const event = type || '';

    if (event.includes('cancel') || event.includes('failed')
        || event.includes('reversed')) {
      return 'bi-x-circle-fill timeline-cancelled';
    }

    if (event.includes('refund')) {
      return 'bi-arrow-counterclockwise timeline-refund';
    }

    return 'bi-check-circle-fill timeline-done';
  }

  // The coloured dot an activity row leads with: `icon` is the glyph, `tone`
  // the class that colours the dot behind it.
  private activityKind(type: string): any {
    const event = type || '';

    if (event.includes('failed') || event.includes('reversed')) {
      return { icon: 'bi-exclamation-lg', tone: 'act-failed' };
    }

    if (event.includes('refund')) {
      return { icon: 'bi-currency-rupee', tone: 'act-failed' };
    }

    if (event.includes('cancel')) {
      return { icon: 'bi-x-lg', tone: 'act-cancelled' };
    }

    if (event === 'settlement_invoices_generated') {
      return { icon: 'bi-file-earmark-text', tone: 'act-booked' };
    }

    if (event.includes('payout')) {
      return { icon: 'bi-check-lg', tone: 'act-done' };
    }

    if (event === 'payment_successful') {
      return { icon: 'bi-currency-rupee', tone: 'act-done' };
    }

    if (event.includes('booked') || event.includes('confirmed')) {
      return { icon: 'bi-calendar2-week', tone: 'act-booked' };
    }

    return { icon: 'bi-check-lg', tone: 'act-initiated' };
  }

  activityIcon(type: string): string {
    return this.activityKind(type).icon;
  }

  activityTone(type: string): string {
    return this.activityKind(type).tone;
  }

  // ── Profile Details tab helpers (ported from lawyers-list.component.ts) ──

  getPlatformLabel(platform: string): string {
    const map: { [key: string]: string } = { android: 'Android', ios: 'iOS', web: 'Web' };
    return map[platform?.toLowerCase()] ?? (platform
      ? platform.charAt(0).toUpperCase() + platform.slice(1) : 'Unknown');
  }

  getPlatformIcon(platform: string): string {
    const map: { [key: string]: string } = {
      android: 'bi-android2',
      ios: 'bi-apple',
      web: 'bi-globe'
    };
    return map[platform?.toLowerCase()] ?? 'bi-device-hdd';
  }

  getPlatformIconColor(platform: string): string {
    const map: { [key: string]: string } = {
      android: '#3ddc84',
      ios:     '#8b5cf6',
      web:     '#0788f2'
    };
    return map[platform?.toLowerCase()] ?? '#6b7280';
  }

  isSectionedMissingFields(fields: any): fields is { section: string; fields: string[] }[] {
    return (
      Array.isArray(fields) &&
      fields.length > 0 &&
      typeof fields[0] === 'object' &&
      'section' in fields[0]
    );
  }

  isFlatMissingFields(fields: any): fields is string[] {
    return (
      Array.isArray(fields) &&
      (fields.length === 0 || typeof fields[0] === 'string')
    );
  }

  getMissingFieldsCount(
    fields: string[] | { section: string; fields: string[] }[] | undefined | null
  ): number {
    if (!fields || fields.length === 0) return 0;
    if (this.isSectionedMissingFields(fields)) {
      return fields.reduce(
        (acc: number, s: { section: string; fields: string[] }) => acc + s.fields.length, 0
      );
    }
    return (fields as string[]).length;
  }

  formatMissingFieldLabel(field: string): string {
    const labelMap: { [key: string]: string } = {
      mobile:                  'Mobile',
      date_of_birth:           'Date of Birth',
      gender:                  'Gender',
      bio_description:         'Bio Description',
      languages_spoken:        'Languages Spoken',
      profilepic_filename:     'Profile Picture',
      bar_council_id:          'Bar Council ID',
      years_of_experience:     'Years of Experience',
      practice_areas:          'Practice Areas',
      services_offered:        'Services Offered',
      registered_address:      'Registered Address',
      mailing_address:         'Mailing Address',
      education:               'Education',
      certifications:          'Certifications',
      awards:                  'Awards',
      consultation_fee:        'Consultation Fee',
      availability:            'Availability',
      court_enrollments:       'Court Enrollments',
      cases_handled:           'Cases Handled',
      currency:                'Currency',
      email:                   'Email',
    };
    return labelMap[field] ?? field.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  }

  formatSectionLabel(section: string): string {
    const map: { [key: string]: string } = {
      profile:      'Profile',
      contact:      'Contact',
      legal:        'Legal',
      subscription: 'Subscription',
      address:      'Address',
      education:    'Education',
      experience:   'Experience',
    };
    return map[section?.toLowerCase()] ??
      section.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  }

  trackByAppointment(_index: number, row: any): string {
    return row.appointment_id;
  }

  trackByPayout(_index: number, row: any): string {
    return row.payout_id;
  }

  trackByActivity(_index: number, row: any): string {
    return row.event_id;
  }

  trackByInvoice(_index: number, row: any): string {
    return row.invoice_id;
  }
}